import LOCALS from 'commons/locals';

type LatestAuthenticationResult = {
  verdict?: string | null;
  authenticityScore?: number | string | null;
  reviewStatus?: 'pending' | 'genuine' | 'fake' | null;
} | null;

export type AuthenticationDisplayDetectionPoint = {
  labelId?: number | null;
  score?: number | null;
  referenceImages?: string[] | null;
  publicUrl?: string | null;
  originalName?: string | null;
};

type AuthenticationDisplayMode =
  | 'normal'
  | 'insufficient'
  | 'few-sample-positive';

type AuthenticationDisplayVerdictKind =
  | 'service'
  | 'insufficient'
  | 'few-sample-positive'
  | 'uncertain'
  | 'genuine'
  | 'suspected-fake'
  | 'none';

type AuthenticationDisplayScoreKind = 'numeric' | 'insufficient' | 'none';

export type AuthenticationDisplaySummary = {
  mode: AuthenticationDisplayMode;
  displayVerdictKind: AuthenticationDisplayVerdictKind;
  displayVerdict: string | null;
  displayScoreKind: AuthenticationDisplayScoreKind;
  displayScore: number | null;
};

type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string | number | object;

const STRONG_DETECTION_POINT_LABEL_IDS = new Set([9, 11, 21, 27, 28]);

const parseAuthenticationScore = (
  score: number | string | null | undefined,
) => {
  if (score === null || score === undefined || score === '') return null;
  const parsedScore = Number(score);
  return Number.isFinite(parsedScore) ? parsedScore : null;
};

export const getAuthenticationDisplaySummary = ({
  latestResult,
  detectionPoints,
}: {
  latestResult?: LatestAuthenticationResult;
  detectionPoints?: AuthenticationDisplayDetectionPoint[] | null;
}): AuthenticationDisplaySummary => {
  const hasValidScore = (
    point: AuthenticationDisplayDetectionPoint,
  ): point is AuthenticationDisplayDetectionPoint & { score: number } =>
    point.score !== null &&
    point.score !== undefined &&
    Number.isFinite(point.score) &&
    point.score >= 0;
  const validDetectionPoints = (detectionPoints || []).filter(
    (point): point is AuthenticationDisplayDetectionPoint & { score: number } =>
      hasValidScore(point),
  );
  const displayScore = parseAuthenticationScore(
    latestResult?.authenticityScore,
  );
  const numericDisplayScoreKind: AuthenticationDisplayScoreKind =
    displayScore === null ? 'none' : 'numeric';
  const reviewStatus = latestResult?.reviewStatus;

  const createSummary = ({
    mode,
    displayVerdictKind,
    displayVerdict = null,
    displayScoreKind = numericDisplayScoreKind,
  }: {
    mode: AuthenticationDisplayMode;
    displayVerdictKind: AuthenticationDisplayVerdictKind;
    displayVerdict?: string | null;
    displayScoreKind?: AuthenticationDisplayScoreKind;
  }): AuthenticationDisplaySummary => ({
    mode,
    displayVerdictKind,
    displayVerdict,
    displayScoreKind,
    displayScore: displayScoreKind === 'numeric' ? displayScore : null,
  });

  if (reviewStatus === 'genuine') {
    return createSummary({
      mode: 'normal',
      displayVerdictKind: 'genuine',
    });
  }

  if (reviewStatus === 'fake') {
    return createSummary({
      mode: 'normal',
      displayVerdictKind: 'suspected-fake',
    });
  }

  const scoredStrongPoints = validDetectionPoints.filter((point) =>
    STRONG_DETECTION_POINT_LABEL_IDS.has(point.labelId || 0),
  );
  const lowStrongPointCount = scoredStrongPoints.filter(
    (point) => point.score < 50,
  ).length;
  const veryLowStrongPointCount = scoredStrongPoints.filter(
    (point) => point.score < 30,
  ).length;
  const highStrongPointCount = scoredStrongPoints.filter(
    (point) => point.score > 80,
  ).length;

  if (lowStrongPointCount >= 2 || veryLowStrongPointCount >= 1) {
    return createSummary({
      mode: 'normal',
      displayVerdictKind: 'suspected-fake',
    });
  }

  if (
    scoredStrongPoints.length >= 2 &&
    highStrongPointCount >= 2 &&
    lowStrongPointCount === 0 &&
    validDetectionPoints.length >= 8
  ) {
    return createSummary({
      mode: 'normal',
      displayVerdictKind: 'genuine',
    });
  }

  return createSummary({
    mode: 'few-sample-positive',
    displayVerdictKind: 'uncertain',
  });
};

export const getAuthenticationDisplayVerdictText = (
  summary: Pick<
    AuthenticationDisplaySummary,
    'displayVerdictKind' | 'displayVerdict'
  >,
  translate: TranslateFn,
) => {
  switch (summary.displayVerdictKind) {
    case 'service':
      return summary.displayVerdict || '';
    case 'insufficient':
      return String(
        i18nSafeTranslate(translate, LOCALS.ai_identify_insufficient_points),
      );
    case 'few-sample-positive':
      return String(
        i18nSafeTranslate(translate, LOCALS.ai_identify_few_sample_positive),
      );
    case 'uncertain':
      return (
        summary.displayVerdict ||
        String(
          i18nSafeTranslate(translate, LOCALS.authentication_uncertain_result),
        )
      );
    case 'genuine':
      return String(
        i18nSafeTranslate(translate, LOCALS.authentication_genuine_result),
      );
    case 'suspected-fake':
      return String(
        i18nSafeTranslate(
          translate,
          LOCALS.authentication_suspected_fake_manual_review,
        ),
      );
    default:
      return '';
  }
};

export const getAuthenticationDisplayScoreText = (
  summary: Pick<
    AuthenticationDisplaySummary,
    'displayScoreKind' | 'displayScore'
  >,
  translate: TranslateFn,
  emptyText = '-',
) => {
  switch (summary.displayScoreKind) {
    case 'numeric':
      return String(
        i18nSafeTranslate(translate, LOCALS.authentication_score_point, {
          score: summary.displayScore,
        }),
      );
    case 'insufficient':
      return String(
        i18nSafeTranslate(translate, LOCALS.ai_identify_insufficient_points),
      );
    default:
      return emptyText;
  }
};

const i18nSafeTranslate = (
  translate: TranslateFn,
  key: string,
  options?: Record<string, unknown>,
) => translate(key, options);
